/**
 * @file element-registry.ts
 * @description Singleton registry for JSON-based element definitions
 * 
 * Design Decisions:
 * - JSON-based definitions for configurability without code changes
 * - Singleton pattern ensures single source of truth
 * - Loads all 27+ element definitions at initialization
 * - Category-based filtering (content, placeholder, layout, composite)
 * - Type-safe access to element defaults, properties, and sections
 * 
 * Element Categories:
 * - content: text, image, header, table, qrcode
 * - placeholder: invoice-number, invoice-date, sender, recipient, etc.
 * - layout: container, cell
 * - composite: invoice-info-block (multi-field elements)
 * 
 * Related:
 * - @see ./element-definition.types.ts - TypeScript interfaces
 * - @see ./*.json - Individual element definitions (27 files)
 * - @see ../utils/element-factory.ts - Creates elements using registry defaults
 * - @see ../../ARCHITECTURE.md#adr-001-json-based-element-definitions
 * 
 * @example
 * const definition = elementRegistry.getDefinition('invoice-number');
 * const defaults = elementRegistry.getDefaults('text');
 * const placeholders = elementRegistry.getDefinitionsByCategory('placeholder');
 */

import type { ElementDefinition } from './element-definition.types';

// Import all element definitions
import textDefinition from './text.json';
import imageDefinition from './image.json';
import absoluteImageDefinition from './absolute-image.json';
import containerDefinition from './container.json';
import cellDefinition from './cell.json';
import headerDefinition from './header.json';
import messageDefinition from './message.json';
import tableDefinition from './table.json';
import qrcodeDefinition from './qrcode.json';
import invoiceNumberDefinition from './invoice-number.json';
import invoiceDateDefinition from './invoice-date.json';
import paymentDueDateDefinition from './payment-due-date.json';
import orderNumberDefinition from './order-number.json';
import leitwegIdDefinition from './leitweg-id.json';
import pageNumberDefinition from './page-number.json';
import smallBusinessNoteDefinition from './small-business-note.json';
import senderDefinition from './sender.json';
import recipientDefinition from './recipient.json';
import customerNumberDefinition from './customer-number.json';
import ibanDefinition from './iban.json';
import swiftDefinition from './swift.json';
import bankNameDefinition from './bank-name.json';
import emailDefinition from './email.json';
import websiteDefinition from './website.json';
import courtDefinition from './court.json';
import hrnDefinition from './hrn.json';
import countryDefinition from './country.json';
import taxNumberDefinition from './tax-number.json';
import vatIdDefinition from './vat-id.json';
import invoiceInfoBlockDefinition from './invoice-info-block.json';

class ElementDefinitionRegistry {
  private definitions: Map<string, ElementDefinition> = new Map();

  constructor() {
    // Register content elements
    this.register(textDefinition as ElementDefinition);
    this.register(imageDefinition as ElementDefinition);
    this.register(absoluteImageDefinition as ElementDefinition);
    this.register(containerDefinition as ElementDefinition);
    this.register(cellDefinition as ElementDefinition);
    this.register(headerDefinition as ElementDefinition);
    this.register(messageDefinition as ElementDefinition);
    this.register(tableDefinition as ElementDefinition);
    this.register(qrcodeDefinition as ElementDefinition);

    // Register placeholder elements - Invoice data
    this.register(invoiceNumberDefinition as ElementDefinition);
    this.register(invoiceDateDefinition as ElementDefinition);
    this.register(paymentDueDateDefinition as ElementDefinition);
    this.register(orderNumberDefinition as ElementDefinition);
    this.register(leitwegIdDefinition as ElementDefinition);
    this.register(pageNumberDefinition as ElementDefinition);
    this.register(smallBusinessNoteDefinition as ElementDefinition);

    // Register placeholder elements - Addresses
    this.register(senderDefinition as ElementDefinition);
    this.register(recipientDefinition as ElementDefinition);
    this.register(customerNumberDefinition as ElementDefinition);

    // Register placeholder elements - Bank information
    this.register(ibanDefinition as ElementDefinition);
    this.register(swiftDefinition as ElementDefinition);
    this.register(bankNameDefinition as ElementDefinition);

    // Register placeholder elements - Company information
    this.register(emailDefinition as ElementDefinition);
    this.register(websiteDefinition as ElementDefinition);
    this.register(courtDefinition as ElementDefinition);
    this.register(hrnDefinition as ElementDefinition);
    this.register(countryDefinition as ElementDefinition);
    this.register(taxNumberDefinition as ElementDefinition);
    this.register(vatIdDefinition as ElementDefinition);

    // Register composite elements
    this.register(invoiceInfoBlockDefinition as ElementDefinition);
  }

  private register(definition: ElementDefinition) {
    this.definitions.set(definition.type, definition);
  }

  /**
   * Get element definition by type
   */
  getDefinition(type: string): ElementDefinition | undefined {
    return this.definitions.get(type);
  }

  /**
   * Get all registered element types
   */
  getAllTypes(): string[] {
    return Array.from(this.definitions.keys());
  }

  /**
   * Get all definitions
   */
  getAllDefinitions(): ElementDefinition[] {
    return Array.from(this.definitions.values());
  }

  /**
   * Get definitions by category
   */
  getDefinitionsByCategory(category: string): ElementDefinition[] {
    return Array.from(this.definitions.values())
      .filter(def => def.category === category);
  }

  /**
   * Get default values for an element type
   */
  getDefaults(type: string): Record<string, unknown> {
    const definition = this.getDefinition(type);
    return definition?.defaults || {};
  }

  /**
   * Get sections for an element type
   */
  getSections(type: string) {
    const definition = this.getDefinition(type);
    return definition?.sections || [];
  }

  /**
   * Get properties for an element type
   */
  getProperties(type: string) {
    const definition = this.getDefinition(type);
    return definition?.properties || [];
  }

  /**
   * Get properties grouped by section
   */
  getPropertiesBySection(type: string) {
    const properties = this.getProperties(type);
    const sections = this.getSections(type);

    const grouped = new Map<string, typeof properties>();

    sections.forEach(section => {
      grouped.set(section.id, []);
    });

    properties.forEach(prop => {
      const sectionProps = grouped.get(prop.section);
      if (sectionProps) {
        sectionProps.push(prop);
      }
    });

    return grouped;
  }
}

// Singleton instance
export const elementRegistry = new ElementDefinitionRegistry();
